import { useEffect, useState } from "react";
import moment from "moment-timezone";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import OwToggle from "../../../shared/OwToggle/OwToggle";
import OwSelectOption from "../../../shared/OwSelectOption/OwSelectOption";
import { LoadingResults } from "../../../shared/LoadingResults/LoadingResults";
import { useAppContextNonNull } from "../../../../contexts/AppContext";
import NotificationLevelEnum from "../../../../models/NotificationLevelEnum";
import ISupplier_Delivery_Schedule from "../../../../models/ISupplier_Delivery_Schedule";
import IVendorDeliveryDeadlineSettings from "../../../../models/DTO/IVendorDeliveryDeadlineSettings";
import IDeliveryDeadlineUser from "../../../../models/DTO/IDeliveryDeadlineUser";
import DeliveryDeadlinesService from "../../../../services/DeliveryDeadlinesService";
import HelperService from "../../../../services/HelperService";
import { DAY_ABBR_KEYS, format12h, sortDays } from "../../../business/BusinessSettings/DeliveryDeadlines/deliveryDeadlineUtils";
import { isCustomerSelectsMode } from "../../../business/BusinessSettings/DeliveryDeadlines/deliveryDateRulesUtils";
import { useIntl } from "react-intl";
import "./DeliveryDeadlinesSection.scss";

type Props = {
    vendorGuid: string;
};

/**
 * Vendor-level Delivery Deadlines controls: assign/clear the schedule template and
 * toggle reminders. Self-contained REST loads/mutations (the surrounding VendorSidebar
 * is socket-based). The vendor timezone field lives in VendorSidebar so it saves with
 * the vendor model.
 */
export const DeliveryDeadlinesSection = (props: Props) => {
    const { userModel, supplierModel, uiContext } = useAppContextNonNull();
    const intl = useIntl();

    const [settings, setSettings] = useState<IVendorDeliveryDeadlineSettings | null>(null);
    const [activeSchedules, setActiveSchedules] = useState<ISupplier_Delivery_Schedule[]>([]);
    const [users, setUsers] = useState<IDeliveryDeadlineUser[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [savingUserId, setSavingUserId] = useState<number | null>(null);

    const load = async () => {
        setIsLoading(true);
        try {
            const [settingsRes, schedulesRes, usersRes] = await Promise.all([
                DeliveryDeadlinesService.getVendorDeadlineSettings(props.vendorGuid, userModel, supplierModel),
                DeliveryDeadlinesService.getDeliverySchedules(userModel, supplierModel),
                DeliveryDeadlinesService.getVendorDeadlineUsers(props.vendorGuid, userModel, supplierModel),
            ]);
            setSettings(settingsRes.deliveryDeadlineSettings);
            setActiveSchedules(schedulesRes.deliverySchedules.filter((s) => s.active));
            setUsers(usersRes.deliveryDeadlineUsers);
        } catch (error) {
            uiContext.showNotification({ message: (error as Error).message, level: NotificationLevelEnum.error });
        } finally {
            setIsLoading(false);
        }
    };

    const toggleUserOptOut = (user: IDeliveryDeadlineUser) => async (e: any) => {
        if (!user.guid) return; // users are addressed by guid; skip if absent (toggle is also disabled)
        // OwToggle emits the new "reminders on" state; opt_out is the inverse.
        const remindersOn = e.target.value as boolean;
        const opt_out = !remindersOn;
        setSavingUserId(user.user_id);
        try {
            await DeliveryDeadlinesService.setUserOptOut(props.vendorGuid, user.guid, opt_out, userModel, supplierModel);
            uiContext.showNotification({ message: "user_reminders_updated", level: NotificationLevelEnum.success });
            setUsers((prev) => prev.map((u) => (u.user_id === user.user_id ? { ...u, delivery_deadline_reminders_opt_out: opt_out } : u)));
        } catch (error) {
            uiContext.showNotification({ message: (error as Error).message, level: NotificationLevelEnum.error });
            await load();
        } finally {
            setSavingUserId(null);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.vendorGuid]);

    const assignSchedule = async (e: any) => {
        const raw = e.target.value;
        const delivery_schedule_guid = raw === "" || raw == null ? null : (raw as string);
        setIsSaving(true);
        try {
            await DeliveryDeadlinesService.assignVendorSchedule(props.vendorGuid, delivery_schedule_guid, userModel, supplierModel);
            uiContext.showNotification({ message: "vendor_schedule_updated", level: NotificationLevelEnum.success });
            await load();
        } catch (error) {
            uiContext.showNotification({ message: (error as Error).message, level: NotificationLevelEnum.error });
        } finally {
            setIsSaving(false);
        }
    };

    const toggleReminders = async (e: any) => {
        const enabled = e.target.value as boolean;
        setIsSaving(true);
        try {
            await DeliveryDeadlinesService.setVendorReminders(props.vendorGuid, enabled, userModel, supplierModel);
            uiContext.showNotification({ message: "vendor_reminders_updated", level: NotificationLevelEnum.success });
            await load();
        } catch (error) {
            uiContext.showNotification({ message: (error as Error).message, level: NotificationLevelEnum.error });
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return <LoadingResults />;
    }

    if (!settings) {
        return null;
    }

    // "Customer selects date" mode (OR-1531): schedule templates do not apply, the customer picks a date
    // under the account's cutoff / lead days / blocked days (Business Settings > Delivery Deadlines).
    if (isCustomerSelectsMode(supplierModel)) {
        return (
            <div className="DeliveryDeadlinesSection">
                <OwLabel title="delivery_mode_customer_selects_vendor_note" className="text-muted mt-2 d-block" />
            </div>
        );
    }

    const previewDays = sortDays(settings.Delivery_Schedule?.Days);

    // Records are addressed by guid; bind the dropdown to the assigned schedule's guid.
    // Ensure the current assignment is always shown even if its template was since deactivated.
    const assigned = settings.Delivery_Schedule;
    const scheduleOptions =
        assigned && !activeSchedules.some((s) => s.guid === assigned.guid) ? [assigned, ...activeSchedules] : activeSchedules;

    return (
        <div className="DeliveryDeadlinesSection">
            <div className="mt-2">
                <OwLabel title="assign_schedule" noBottomMargin={true} />
                <OwSelectOption
                    name="delivery_schedule_guid"
                    options={scheduleOptions}
                    valueProp="guid"
                    titleProp="name"
                    selectedOption={settings.Delivery_Schedule}
                    selectedOptionProp="guid"
                    fn={assignSchedule}
                    showBlankFirst={true}
                    isDisabled={isSaving}
                />
                <OwLabel title="assign_schedule_helper" className="text-muted small" />
            </div>

            <div className="mt-2">
                <OwToggle
                    fn={toggleReminders}
                    name="delivery_deadline_reminders"
                    label="delivery_deadline_reminders"
                    active={!!settings.delivery_deadline_reminders}
                    labelOnRight={true}
                    disabled={isSaving}
                />
            </div>

            {settings.delivery_schedule_assigned_at && (
                <div className="mt-2 text-muted small">
                    <OwLabel title="schedule_assigned_at" noBottomMargin={true} skipTranslate={false} />
                    <span className="ms-1">{moment(settings.delivery_schedule_assigned_at).format("lll")}</span>
                </div>
            )}

            {previewDays.length > 0 && (
                <div className="mt-2">
                    <OwLabel title="schedule_preview" className="fw-bold" noBottomMargin={true} />
                    <ul className="list-unstyled small mb-0">
                        {previewDays.map((day) => (
                            <li key={day.id}>
                                <span className="fw-bold">{intl.formatMessage({ id: DAY_ABBR_KEYS[day.delivery_day_of_week] })}</span>
                                {": "}
                                {intl.formatMessage({ id: "hard_deadline" })}{" "}
                                {intl.formatMessage({ id: DAY_ABBR_KEYS[day.hard_deadline_day_of_week] })}{" "}
                                {format12h(day.hard_deadline_time)}
                                {day.soft_reminder_day_of_week != null && day.soft_reminder_time
                                    ? `, ${intl.formatMessage({ id: "soft_reminder" })} ${intl.formatMessage({
                                          id: DAY_ABBR_KEYS[day.soft_reminder_day_of_week],
                                      })} ${format12h(day.soft_reminder_time)}`
                                    : ""}
                                {day.minimum_order_total != null
                                    ? `, ${intl.formatMessage({ id: "minimum_order_total" })}: ${HelperService.formatCurrency({
                                          value: Number(day.minimum_order_total),
                                      })}`
                                    : ""}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {users.length > 0 && (
                <div className="mt-3">
                    <OwLabel title="user_reminder_opt_outs" className="fw-bold" />
                    <div className="table-responsive">
                        <table className="table table-sm align-middle">
                            <thead>
                                <tr>
                                    <th>
                                        <OwLabel title="name" noBottomMargin={true} />
                                    </th>
                                    <th>
                                        <OwLabel title="email" noBottomMargin={true} />
                                    </th>
                                    <th className="text-end">
                                        <OwLabel title="delivery_deadline_reminders" noBottomMargin={true} />
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((user) => (
                                    <tr key={user.user_id}>
                                        <td>{user.name || "—"}</td>
                                        <td>{user.email || "—"}</td>
                                        <td className="text-end">
                                            <OwToggle
                                                fn={toggleUserOptOut(user)}
                                                name={`user_reminders_${user.user_id}`}
                                                label=""
                                                active={!user.delivery_deadline_reminders_opt_out}
                                                disabled={savingUserId === user.user_id || !user.guid}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DeliveryDeadlinesSection;
